import { cloneDeep, camelCase, flatten, trimStart } from 'lodash'

import { MAIN_SERVER } from './instance'
import { EDUCATION_PRODUCT_LIST, PRODUCT_LIST_PAID } from '../common/constants/ProductConstants'

export const GET_PAYMENT_PRODUCTS = (() => {
    const featureOrderByName = [
        'Full customization',
        'Basic statistics',
        'Translations',
        'Pageviews / month',
        'Websites',
        'Premium templates / month',
        'Ads free',
        'Google analytics',
        'Facebook pixel',
        'Lead collection form',
        'Lead contacts / month',
        'Custom project url',
        'Advanced statistics',
        'White label',
        'Priority response',
        'Personal onboarding',
        'Gamification',
        'Custom CSS styles',
        'API integration',
    ]

    const featureOrderByNameEducation = [
        'Student visits / month',
        'Maximum projects count',
        'Label Interacty Education',
        'Share projects by link',
        'Translations',
        'Learning Quizzes',
        'Memory Games',
        'Puzzle Games',
        'Timelines',
        'Premium templates / month',
        'Student leaderboards',
    ]

    const featureTypeMapEducation = {
        REVIEWS_COUNT: 'Student visits / month',
        PROJECTS_COUNT: 'Maximum projects count',
        PREMIUM_TEMPLATES_COUNT: 'Premium templates / month',
        LABEL_INTERACTY_EDUCATION: 'Label Interacty Education',
        SHARE_CONTENT: 'Share projects by link',
        LEARNING_QUIZZES: 'Learning Quizzes',
        MEMORY_GAME: 'Memory Games',
        PUZZLE_GAME: 'Puzzle Games',
        TIMELINE: 'Timelines',
        GAMIFICATION: 'Student leaderboards',
        TRANSLATIONS: 'Translations',
    }

    const optionAddons = {
        advancedStatistics: { under: 'Engagement, geography, conversions' },
        whiteLabel: { under: 'No Interacty logo' },
        priorityResponse: { under: 'Technical support' },
    }

    const countMaxOptions = products => Math.max(...products.map(({ featureProducts }) => featureProducts.length))

    const featureTypeMap = {
        REVIEWS_COUNT: 'Pageviews / month',
        NO_LOGO: 'White label',
        NO_ADVERTISEMENT: 'Ads free',
        FULL_CUSTOMIZATION: 'Full customization',
        FACEBOOK_PIXEL: 'Facebook pixel',
        GOOGLE_ANALYTICS: 'Google analytics',
        LEAD_CONTACTS: 'Lead contacts / month',
        BASIC_STATISTIC: 'Basic statistics',
        LEAD_COLLECTION_FORM: 'Lead collection form',
        PRIORITY_SUPPORT: 'Priority response',
        FULL_STATISTICS: 'Advanced statistics',
        CUSTOM_PROJECT_URL: 'Custom project url',
        GAMIFICATION: 'Gamification',
        WEBSITES: 'Websites',
        PERSONAL_ONBOARDING: 'Personal onboarding',
        SDK_INTEGRATION: 'API integration',
        CUSTOM_CSS_STYLES: 'Custom CSS styles',
        PREMIUM_TEMPLATES_COUNT: 'Premium templates / month',
        TRANSLATIONS: 'Translations',
    }

    const getFeatureName = (name, featureType, isEducation = false) => {
        const displayName = isEducation ? featureTypeMapEducation[featureType] : featureTypeMap[featureType]
        return displayName ? displayName : trimStart(name, '-1350 ')
    }

    const getFeatureNameMap = (products, isEducation = false) => {
        const features = products.map(({ featureProducts }) =>
            featureProducts
                .map(({ name, featureType }) => getFeatureName(name, featureType, isEducation))
                .map(name => [camelCase(name), name]),
        )
        return Object.fromEntries(flatten(features))
    }

    const mapFeatureList = (featureNameMap, options, isEducation) => {
        const mappedOptions = Object.fromEntries(
            options.map(feature => {
                feature.name = getFeatureName(feature.name, feature.featureType, isEducation)

                if (feature.name === 'Lead contacts / month') {
                    const result = (feature.description.match(/[^a-z>]\ *([.0-9])*\d/g) || [])[0]

                    feature.productOptionValue = parseFloat(result) || 0
                    feature.hasOption = true
                }

                const key = camelCase(feature.name)

                return [key, feature]
            }),
        )

        const emptyAllOptions = Object.fromEntries(
            Object.entries(featureNameMap).map(([key, name]) => [key, { name, enabled: false }]),
        )

        const featureMap = { ...emptyAllOptions, ...mappedOptions }

        return Object.entries(featureMap).map(([key, feature]) =>
            optionAddons[key] ? { ...feature, ...optionAddons[key] } : feature,
        )
    }

    const productSort = unsorted =>
        unsorted.filter(product =>
            [...Object.values(PRODUCT_LIST_PAID), ...Object.values(EDUCATION_PRODUCT_LIST)].includes(
                product.productCode,
            ),
        )

    const featureSort = (unsorted, isEducation = false) => {
        let sorted = []
        let featureOrder = isEducation ? featureOrderByNameEducation : featureOrderByName
        featureOrder.forEach(orderName => {
            const feature = unsorted.find(({ name }) => name === orderName)
            if (feature) sorted.push(feature)
        })
        return sorted
    }

    const hasFeature = (product, featureType) =>
        !!product.featureProducts.find(feature => feature.featureType === featureType)
    const createFakeFeature = (name, type, id) => ({
        billingCycleType: null,
        billingCycleValue: null,
        description: `<p>${name}</p>`,
        enabled: true,
        featureType: type,
        hasOption: false,
        id,
        name,
        productCode: `D00000000${id}`,
        productId: id,
        productImages: [],
        productOptionValue: null,
        productType: 'FEATURE',
    })

    return async (isEducation = false, userId = null) => {
        let products
        if (userId) {
            products = await MAIN_SERVER({
                method: 'get',
                url: `/products/by-user/${userId}`,
            })
            if (products.find(product => product.productCode === EDUCATION_PRODUCT_LIST.EducationFree)) {
                isEducation = true
            }
        } else if (isEducation) {
            products = await MAIN_SERVER({
                method: 'get',
                url: '/products/education',
            })
        } else {
            products = await MAIN_SERVER({
                method: 'get',
                url: '/products',
            })
        }

        // Temporarily HARDCODE
        /* While the required data is not on the server, we need to add this data to the server response.
         * After this data appears on the server, this code will not interfere as it checks the featureType property
         * but the need for it will disappear.
         * */
        // TODO Remove this code after the required data appears on the server
        for (let ind in products) {
            const product = products[+ind]
            if (isEducation) {
                if (!hasFeature(product, 'SHARE_CONTENT')) {
                    product.featureProducts.push(createFakeFeature('Share projects by link', 'SHARE_CONTENT', 1))
                }

                if (!hasFeature(product, 'LEARNING_QUIZZES')) {
                    product.featureProducts.push(createFakeFeature('Learning Quizzes', 'LEARNING_QUIZZES', 2))
                }

                if (!hasFeature(product, 'MEMORY_GAME')) {
                    product.featureProducts.push(createFakeFeature('Learning Memory Games', 'MEMORY_GAME', 3))
                }

                if (!hasFeature(product, 'PUZZLE_GAME')) {
                    product.featureProducts.push(createFakeFeature('Puzzle Games', 'PUZZLE_GAME', 4))
                }

                if (!hasFeature(product, 'TIMELINE')) {
                    product.featureProducts.push(createFakeFeature('Timelines', 'TIMELINE', 5))
                }
            } else {
                if (!hasFeature(product, 'CUSTOM_CSS_STYLES') && product.name.includes('Enterprise')) {
                    product.featureProducts.push(createFakeFeature('Custom CSS styles', 'CUSTOM_CSS_STYLES', 6))
                }
            }

            if (!hasFeature(product, 'TRANSLATIONS')) {
                product.featureProducts.push(createFakeFeature('Translations', 'TRANSLATIONS', 666))
            }
        }
        //------

        const totalOptions = countMaxOptions(products)
        const featureNameMap = getFeatureNameMap(products, isEducation)

        const preparedProducts = products.map(item => {
            item.totalOptions = totalOptions
            item.activeOptions = item.featureProducts.length
            item.featureProducts = featureSort(
                mapFeatureList(featureNameMap, item.featureProducts, isEducation),
                isEducation,
            )
            item.name = item.name.split(' ')[0]
            item.price = item.purchasePrice

            return item
        })

        const month = productSort(
            preparedProducts
                .filter(({ billingCycleValue }) => billingCycleValue === 1 || billingCycleValue === -1)
                .map(cloneDeep)
                .map(item => {
                    item.billingCycleValue = 1

                    return item
                }),
        )

        const annual = productSort(
            preparedProducts
                .filter(({ billingCycleValue }) => billingCycleValue === 12 || billingCycleValue === -1)
                .map(cloneDeep)
                .map(item => {
                    item.billingCycleValue = 12

                    return item
                }),
        )

        return { annual, month }
    }
})()

export const GET_PAYMENT_PRODUCTS_EDUCATION = () => GET_PAYMENT_PRODUCTS(true, null)

export const GET_PAYMENT_PRODUCTS_BY_USER_ID = userId => GET_PAYMENT_PRODUCTS(false, userId)

export const GET_MERCHANT_CODE = () =>
    MAIN_SERVER({
        method: 'get',
        url: '/products/merchant',
    })

export const GET_PRODUCT_CHANGED_RULES = () => MAIN_SERVER({ method: 'get', url: '/products/product-changing-rules' })

export const SET_EDUCATION_FREE_SUBSCRIPTION = id =>
    MAIN_SERVER({ method: 'patch', url: `/subscriptions/${id}/product/education` })
